import React, { FC, useMemo, SyntheticEvent, ReactNode } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  AutocompleteRenderGetTagProps,
  TextField,
  Button,
  Chip,
  Grid,
} from '@mui/material';
import { DeviceReadingsSelectorSpecificDevice } from './DeviceReadingsSelector';
import { ReadingsRequestSensorDto, ReadingsRequestDto } from '@thingslog/repositories';
import { SensorDataDeviceSensorOption } from '../../models';
import PeriodPicker from '../../../../components/DatePicker/PeriodPicker';
import { useTranslation } from 'react-i18next';

export const DeviceReadingsSelectorSpecificComponent: FC<DeviceReadingsSelectorSpecificDevice> = (
  props: DeviceReadingsSelectorSpecificDevice
) => {
  const { t } = useTranslation();
  const {
    variant,
    payload,
    onPayloadChange,
    onSubmit,
    deviceNumber,
    sensorOptions,
    blockInteractions,
    sensorNameSnapshotter,
  } = props;

  const currentlySelectedSensorOptions = useMemo(
    () =>
      payload.deviceSensorList
        .filter(
          (sensor: ReadingsRequestSensorDto) =>
            sensor.deviceNumber === deviceNumber &&
            sensorOptions.some(
              (option: SensorDataDeviceSensorOption) => option.sensorIndex === sensor.sensorIndex
            )
        )
        .map((sensor: ReadingsRequestSensorDto): number => sensor.sensorIndex),
    [payload.deviceSensorList, deviceNumber, sensorOptions]
  );

  const selectedSensorOptions = useMemo(
    () =>
      sensorOptions.filter((option: SensorDataDeviceSensorOption) =>
        currentlySelectedSensorOptions.includes(option.sensorIndex)
      ),
    [sensorOptions, currentlySelectedSensorOptions]
  );

  const handleSensorChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: SensorDataDeviceSensorOption[]
  ): void => {
    // Remove all sensors for this device from the payload
    const updatedDeviceSensorList = payload.deviceSensorList.filter(
      (sensor: ReadingsRequestSensorDto) => sensor.deviceNumber !== deviceNumber
    );

    // Add the newly selected sensors for this device
    const newSensors: ReadingsRequestSensorDto[] = newValue.map(
      (option: SensorDataDeviceSensorOption) => ({
        deviceNumber,
        sensorIndex: option.sensorIndex,
      })
    );

    const updatedPayload: ReadingsRequestDto = {
      ...payload,
      deviceSensorList: [...updatedDeviceSensorList, ...newSensors],
    };

    onPayloadChange(updatedPayload);
  };

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md="auto">
        <Grid container spacing={2} alignItems="center">
          <PeriodPicker alwaysShowDatePickers />
        </Grid>
      </Grid>
      <Grid item xs={12} md>
        <Autocomplete
          multiple
          size="small"
          disabled={blockInteractions}
          options={sensorOptions}
          value={selectedSensorOptions}
          onChange={handleSensorChange}
          getOptionLabel={(option: SensorDataDeviceSensorOption): string =>
            `${option.sensorName} (Index: ${option.sensorIndex})`
          }
          filterOptions={(
            options: SensorDataDeviceSensorOption[],
            state: { inputValue: string }
          ): SensorDataDeviceSensorOption[] => {
            const searchValue = state.inputValue.toLowerCase();
            return options.filter(
              (option: SensorDataDeviceSensorOption) =>
                option.sensorName.toLowerCase().includes(searchValue) ||
                option.sensorIndex.toString().includes(searchValue)
            );
          }}
          renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
            <TextField
              {...params}
              variant="outlined"
              label={t('sensors_data_select_sensors')}
              placeholder={t('sensors_data_search_sensor')}
            />
          )}
          renderTags={(
            value: SensorDataDeviceSensorOption[],
            getTagProps: AutocompleteRenderGetTagProps
          ): ReactNode =>
            value.map((option: SensorDataDeviceSensorOption, index: number) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={`${option.sensorName} (${option.sensorIndex})`}
                  {...tagProps}
                  size="small"
                />
              );
            })
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md="auto">
        <Button
          variant="contained"
          disabled={blockInteractions}
          onClick={onSubmit}
          size="medium"
          fullWidth
          disableElevation
        >
          {t('sensors_data_submit')}
        </Button>
      </Grid>
    </Grid>
  );
};
